import { useState, useEffect, type FC } from "react";

import { Link } from "react-router-dom";
import { Check, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/utils/core";
import { FieldError } from "@/shared/components/ui/FieldError";
import { GoogleOAuthSection } from "@/shared/components/ui/GoogleOAuthSection";
import { PasswordRequirement } from "@/shared/components/ui/PasswordRequirement";
import { validatePassword } from "@/shared/utils/passwordValidation";
import { LAWYER_ROUTES, LEGAL_ROUTES } from "@/shared/constants/paths";
import { startGoogleOAuth } from "@/shared/utils/googleOAuth";

interface CreateAccountStepProps {
  prefillEmail?: string;
  inviteToken?: string;
  onSubmit: (email: string, password: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
  onError: (message: string) => void;
}

export const CreateAccountStep: FC<CreateAccountStepProps> = ({
  prefillEmail,
  inviteToken,
  onSubmit,
  isLoading,
  error,
  onClearError,
  onError,
}) => {
  const [email, setEmail] = useState(prefillEmail ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  useEffect(() => {
    if (prefillEmail) {
      setEmail(prefillEmail);
    }
  }, [prefillEmail]);

  const {
    hasMinLength,
    hasMixedCase,
    hasNumber,
    allMet: allRequirementsMet,
  } = validatePassword(password);
  const isValid = email.includes("@") && allRequirementsMet;

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitted(true);
    onClearError();
    if (!isValid) return;
    try {
      await onSubmit(email, password);
    } catch {
      // Error handled by parent
    }
  };

  const handleGoogleSignUp = async () => {
    if (inviteToken) {
      sessionStorage.setItem("lawyer_invite_token", inviteToken);
    }
    const oauthError = await startGoogleOAuth({
      type: "lawyer-signup",
      redirectPath: LAWYER_ROUTES.SIGNUP_PROFILE,
    });
    if (oauthError) {
      onError(oauthError);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <h2 className="mb-2 text-center">Create your account</h2>

      <GoogleOAuthSection label="Sign up with Google" onGoogleClick={handleGoogleSignUp} />

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-primary">Professional email</label>
        <Input
          type="email"
          placeholder="example@email.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setSubmitted(false);
          }}
          disabled={!!prefillEmail}
          className={submitted && !email.includes("@") ? "border-destructive" : ""}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-primary">Password</label>
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setSubmitted(false);
            }}
            onFocus={() => setPasswordFocused(true)}
            className={cn("pr-10", submitted && !allRequirementsMet && "border-destructive")}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {(passwordFocused || password.length > 0) && (
        <div className="flex flex-col gap-2">
          {allRequirementsMet ? (
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" strokeWidth={3} />
              <span className="text-sm text-primary">All requirements met</span>
            </div>
          ) : (
            <>
              <PasswordRequirement met={hasMinLength} text="At least 8 characters" />
              <PasswordRequirement met={hasMixedCase} text="Upper & lowercase letters" />
              <PasswordRequirement met={hasNumber} text="At least one number" />
            </>
          )}
        </div>
      )}

      <FieldError
        message={
          error ??
          (submitted && !isValid
            ? !email.includes("@")
              ? "Enter a valid email address"
              : "Please meet all password requirements"
            : null)
        }
      />

      <Button
        type="button"
        variant="brand"
        rounded={true}
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            Create account <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>

      <p className="text-center text-2xs text-secondary">
        By signing up, you agree to our{" "}
        <Link
          to={LEGAL_ROUTES.TERMS_OF_SERVICE}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary underline"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          to={LEGAL_ROUTES.PRIVACY_POLICY}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary underline"
        >
          Privacy Policy
        </Link>
        .
      </p>

      <p className="text-center text-sm text-secondary">
        Already have an account?{" "}
        <Link to={LAWYER_ROUTES.LOGIN} className="text-brand underline">
          Sign in
        </Link>
      </p>
    </div>
  );
};
